import { Box, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel } from '@chakra-ui/react';
import { lookup } from '@sendpayments/js-utils';
import React, { ReactNode } from 'react';
import { FormatOptionLabelMeta } from 'react-select';
import { OptionType, Select, SelectedOption, SelectProps, ValueContainer } from '..';
import { CurrencyFlagCircular } from '../../CurrencyFlag';

interface PhoneExt {
  phoneExtension?: string;
  country: string;
  currencyCode: string;
  currencyName?: string;
}

interface PhoneExtOptionType extends OptionType {
  currencyCode: string;
}

// create a list of Options from the phone extensions
const phoneExtensions: PhoneExtOptionType[] = Object.entries<PhoneExt>(lookup).reduce<PhoneExtOptionType[]>(
  (acc = [], [, curr]) => {
    if (curr.phoneExtension) {
      const option: PhoneExtOptionType = {
        value: curr.phoneExtension,
        label: curr.country,
        currencyCode: curr.currencyCode,
      };
      acc.push(option);
    }
    return acc;
  },
  [],
);

interface PhoneExtOptionProps {
  phoneExtension: string;
  country: string;
  currencyCode: string;
}

export const PhoneExtOption = ({ phoneExtension, country, currencyCode }) => (
  <Flex direction="row">
    <Box w={8}>
      <CurrencyFlagCircular currencyName={currencyCode} />
    </Box>
    <Box minW={10} fontWeight="bold">
      {phoneExtension}
    </Box>
    <Box>{country}</Box>
  </Flex>
);

export type PhoneExtSelectProps = SelectProps & {
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
  onSelect: (value: OptionType | null) => void;
};

export const PhoneExtSelect = ({
  id,
  label,
  placeholder,
  value,
  onSelect,
  helperText,
  isInvalid = false,
  errorText,
  width = '100%',
  menuMinWidth = width,
  isDisabled,
}: PhoneExtSelectProps) => {
  const formatOptionLabel: (option: OptionType, labelMeta: FormatOptionLabelMeta<OptionType, false>) => ReactNode = (
    option,
    { context, selectValue },
  ) => {
    if (context === 'value' && selectValue) {
      return <Box fontWeight="bold">{option.value}</Box>;
    } else if (context === 'menu') {
      return selectValue && selectValue[0]?.value === option.value ? (
        <SelectedOption>
          <PhoneExtOption phoneExtension={option.value} country={option.label} currencyCode={option.currencyCode} />
        </SelectedOption>
      ) : (
        <PhoneExtOption phoneExtension={option.value} country={option.label} currencyCode={option.currencyCode} />
      );
    }
  };
  return (
    <FormControl id={id} isInvalid={isInvalid}>
      {label && (
        <FormLabel fontSize="sm" marginBottom="5px">
          {label}
        </FormLabel>
      )}
      <Select
        styles={{}}
        placeholder={placeholder}
        value={value}
        options={phoneExtensions}
        onChange={onSelect}
        width={width}
        menuMinWidth={menuMinWidth}
        isInvalid={isInvalid}
        formatOptionLabel={formatOptionLabel}
        components={{ ValueContainer }}
        isDisabled={isDisabled}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      <FormErrorMessage>{errorText}</FormErrorMessage>
    </FormControl>
  );
};
